<template>
    <div class="timer-container">
        <div
            class="timer-bar"
            :style="{
                width: `${progressPercentage}%`,
            }"
        />
    </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';
import {
    ref, computed, onUnmounted, watch,
} from 'vue';
import { getNextPeriodBoundary } from '@/utils/periodUtils.ts';

dayjs.extend(duration);

const props = defineProps<{
    useTradingPeriod?: boolean;
}>();

const emit = defineEmits(['finished']);

const HALF_HOUR_SECONDS = 1800;

const totalSeconds = ref<Duration>(dayjs.duration(0, 'seconds'));
const nextHalfHour = ref<dayjs.Dayjs>(dayjs());
let interval: ReturnType<typeof setInterval> | null = null;

const progressPercentage = computed<number>(() => {
    const remaining = totalSeconds.value.asSeconds();
    const percentage = ((HALF_HOUR_SECONDS - remaining) / HALF_HOUR_SECONDS) * 100;

    return Math.min(Math.max(percentage, 0), 100);
});

function start (): void {
    interval = setInterval(() => {
        if (totalSeconds.value.asSeconds() > 0) {
            setCountdownExpiryInSeconds();
        } else {
            emit('finished');
            if (interval) clearInterval(interval);
        }
    }, 1000);
}

function setCountdownExpiryTimeInSeconds (): void {
    nextHalfHour.value = getNextPeriodBoundary(dayjs(), props.useTradingPeriod);
}

function initialise (): void {
    setCountdownExpiryTimeInSeconds();
    setCountdownExpiryInSeconds();
    start();
}

function setCountdownExpiryInSeconds (): void {
    totalSeconds.value = dayjs.duration(nextHalfHour.value.diff(dayjs(), 'seconds'), 'seconds');
}

function restart (): void {
    if (interval) clearInterval(interval);
    setCountdownExpiryTimeInSeconds();
    setCountdownExpiryInSeconds();
    start();
}

defineExpose({
    restart,
});

watch(() => props.useTradingPeriod, restart);

onUnmounted((): void => {
    if (interval) clearInterval(interval);
});

initialise();
</script>

<style lang="scss" scoped>
.timer-container {
    width: 100%;
    height: 4px;
    background-color: var(--neutral500);
    border-radius: var(--borderRadiusInner);
    overflow: hidden;
    .timer-bar {
        height: 100%;
        transition: width 0.1s linear;
        background-color: var(--lcpWatermelon);
    }
}
</style>
